import React from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";

/** Fastest advertised tier in the catalogue; the meter's full-scale point. */
const SCALE_MAX_MBPS = 8000;
const SCALE_MIN_MBPS = 10;

/**
 * Log-scaled fill, so 100 Mbps, 1 Gbps and 8 Gbps all read as visibly
 * different steps instead of the slow tiers collapsing to a sliver.
 */
export const getSpeedFillPercent = (mbps: number) => {
  const clamped = Math.min(Math.max(mbps, SCALE_MIN_MBPS), SCALE_MAX_MBPS);
  const ratio =
    Math.log(clamped / SCALE_MIN_MBPS) /
    Math.log(SCALE_MAX_MBPS / SCALE_MIN_MBPS);
  return Math.max(6, Math.round(ratio * 100));
};

type Props = {
  download: number | null;
  upload: number | null;
  downloadLabel: string;
  uploadLabel: string;
  /** Formatted speeds, e.g. "1 Gbps". */
  downloadValue: string;
  uploadValue: string;
  unknownLabel: string;
  className?: string;
};

/** Two stacked bars comparing download and upload on a shared log scale. */
const InternetSpeedMeter: React.FC<Props> = ({
  download,
  upload,
  downloadLabel,
  uploadLabel,
  downloadValue,
  uploadValue,
  unknownLabel,
  className,
}) => {
  const rows = [
    {
      key: "down",
      icon: ArrowDown,
      label: downloadLabel,
      value: download,
      text: download !== null ? downloadValue : unknownLabel,
      fill: "bg-accent",
    },
    {
      key: "up",
      icon: ArrowUp,
      label: uploadLabel,
      value: upload,
      text: upload !== null ? uploadValue : unknownLabel,
      fill: "bg-secondary border border-zinc-300",
    },
  ];

  return (
    <dl className={cn("flex flex-col gap-1.5", className)}>
      {rows.map((row) => {
        const Icon = row.icon;
        return (
          <div key={row.key} className="flex items-center gap-2">
            <dt className="flex w-24 flex-none items-center gap-1 text-xs text-muted-foreground">
              <Icon className="size-3.5" aria-hidden />
              {row.label}
            </dt>
            <dd className="flex min-w-0 flex-1 items-center gap-2">
              <span className="relative h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                {row.value !== null && (
                  <span
                    className={cn(
                      "absolute inset-y-0 left-0 rounded-full",
                      row.fill,
                    )}
                    style={{ width: `${getSpeedFillPercent(row.value)}%` }}
                  />
                )}
              </span>
              <span className="w-[4.5rem] flex-none text-right text-xs font-semibold tabular-nums">
                {row.text}
              </span>
            </dd>
          </div>
        );
      })}
    </dl>
  );
};

export default InternetSpeedMeter;
